
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Icon } from '@blueprintjs/core';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import type { Optional, Required as SetRequiredProps } from 'utility-types';
import type { ArrayInputFields } from '@pinto/react-form';

type DefinedListProps<T = any> = {
    /**
     * Array-like element. `fields` from a FieldArray is supported
     * It needs to have a `.length` and a `.map` function
     */
    readonly data: Array<T> | ArrayInputFields;
    /**
     * Main method that renders each item in the `data`
     * Called once for every element in `data`
     */
    renderItem: (props: RenderMethodProps<T>) => JSX.Element | null;
    /**
     * Called when the remove button is pressed
     * It should update `data` by removing this element
     */
    onItemRemove: (props: RenderMethodProps<T>) => any;
    /**
     * Called when the "Add More" button is pressed
     * It should update data with a new element
     */
    onAddMore: (props: RenderMethodProps<T>) => any;
    /**
     * Whether the "Add More" functionality is disabled entirely
     */
    canAddMore: boolean;
    /**
     * Enables sorting and called when the elements are sorted
     */
    onSort: (drag: DropResult) => any;
    renderAddMore: (props: Omit<RenderMethodProps<T>, 'index'>) => JSX.Element | null;
    renderAddMoreContainer: (props: Omit<RenderMethodProps<T>, 'index'>) => JSX.Element | null;
    renderItemContainer: (props: RenderMethodProps<T>) => JSX.Element | null;
    renderItemRemoveButton: (props: RenderMethodProps<T>) => JSX.Element | null;
    renderItemDragHandle: (props: RenderMethodProps<T>) => JSX.Element | null;
};

interface RenderMethodProps<T> extends DefinedListProps<T> {
    index: number;
    isSortable: boolean;
    item: T;
    [key: string]: any;
};

type OptionalListProps<T> = Optional<DefinedListProps<T>, keyof DefinedListProps>;

export type ListProps<T> = SetRequiredProps<OptionalListProps<T>, 'data' | 'renderItem' | 'onItemRemove'>;

export interface ListFieldProps extends Omit<ListProps<string>, 'data'> {
    fields: ArrayInputFields;
    /**
     * Called when the "Add More" button is pressed to create the new entry
     */
    getAddMore?: () => any;
    sortable?: boolean;
}

export type ListFieldComponentProps = Omit<ListFieldProps, 'onItemRemove' | 'onAddMore'>;

export class List<T = any> extends React.Component<ListProps<T> & { [key: string]: any }> {

    static propTypes = {
        data: PropTypes.oneOfType([
            PropTypes.array,
            PropTypes.shape({
                map: PropTypes.func.isRequired,
                length: PropTypes.number.isRequired,
            }).isRequired,
        ]).isRequired,
        renderItem: PropTypes.func.isRequired,
        onItemRemove: PropTypes.func.isRequired,
        onAddMore: PropTypes.func.isRequired,
        canAddMore: PropTypes.bool,
        onSort: PropTypes.func,

        renderAddMore: PropTypes.func.isRequired,
        renderAddMoreContainer: PropTypes.func.isRequired,
        renderItemContainer: PropTypes.func.isRequired,
        renderItemRemoveButton: PropTypes.func.isRequired,
    };

    static defaultProps: Partial<ListProps<any>> = {
        renderItemContainer: props =>
            <Item {...props.containerProps}>
                {props.renderItemDragHandle(props)}
                {props.renderItem(props)}
                {props.renderItemRemoveButton(props)}
            </Item>,

        renderAddMore: props =>
            <Button icon='add' intent='primary' onClick={() => props.onAddMore(props)}>
                Add More
            </Button>,

        renderAddMoreContainer: props =>
            props.renderAddMore(props),

        renderItemDragHandle: props =>
            <Icon icon='drag-handle-vertical' {...props.dragHandleProps} />,

        renderItemRemoveButton: props =>
            <RemoveButton
                Tag={Button}
                minimal
                intent='danger'
                icon='trash'
                onClick={() => props.onItemRemove(props)}
            />,
    };

    get isSortable() { return !!this.props.onSort && this.props.data.length > 1; }

    render() {
        const {
            data,
            canAddMore,
            renderAddMoreContainer,
            renderItemContainer,
            onSort,
        }: DefinedListProps<T> = this.props as DefinedListProps<T>;
        const baseProps = {
            ...(this.props as DefinedListProps<T>),
            isSortable: this.isSortable,
        };

        const addMore = canAddMore && renderAddMoreContainer(baseProps);

        if (!this.isSortable) {
            baseProps.renderItemDragHandle = () => null;

            return <React.Fragment>
                {(data as T[]).map((item, index) =>
                    renderItemContainer({
                        ...baseProps,
                        item,
                        index,
                        containerProps: {
                            key: index,
                        },
                    })
                )}
                {addMore}
            </React.Fragment>;
        }

        return <DragDropContext onDragEnd={onSort as any}>
            <Droppable droppableId='droppableId'>
                {(droppableProvided, droppableSnapshot) =>
                    <Sortable ref={droppableProvided.innerRef}>
                        {(data as T[]).map((item, index) =>
                            <Draggable key={index} draggableId={String(index)} index={index}>
                                {(draggableProvided, draggableSnapshot) => (
                                    renderItemContainer({
                                        ...baseProps,
                                        key: index,
                                        item,
                                        index,
                                        dragHandleProps: draggableProvided.dragHandleProps,
                                        containerProps: {
                                            ref: draggableProvided.innerRef,
                                            ...draggableProvided.draggableProps,
                                        },
                                    }) as any
                                )}
                            </Draggable>
                        )}

                        {droppableProvided.placeholder}
                        {addMore}
                    </Sortable>
                }
            </Droppable>
        </DragDropContext>
    }

}

export default List;

export class ListField extends React.Component<ListFieldComponentProps> {

    static propTypes = {
        fields: PropTypes.shape({
            map: PropTypes.func.isRequired,
            swap: PropTypes.func.isRequired,
            length: PropTypes.number.isRequired,
        }).isRequired,
        getAddMore: PropTypes.func,
        sortable: PropTypes.bool,
    };

    static defaultProps: Partial<ListFieldProps> = {
        getAddMore: () => null,
        onAddMore: ({ data, getAddMore }) => data.push(getAddMore(data)),
        onItemRemove: ({ data, index }) => (data as ArrayInputFields).remove(index),
    };

    handleSort = ({ source, destination }: DropResult) =>
        this.props.fields.swap(source.index, destination!.index);

    render() {
        const { fields, sortable, ...props } = this.props as ListFieldProps;

        if (sortable) props.onSort = this.handleSort;

        return <List {...props} data={fields as ArrayInputFields} />;
    }

}


require('!style-loader/lib/addStyles.js')([[ module.id, ".flex {\n  display: flex;\n}\n.flex.flex-space-between {\n  align-items: space-between;\n}\n.center {\n  text-align: center;\n}\n.bold {\n  font-weight: bold;\n}\n.mt0 {\n  margin-top: 0rem !important;\n}\n.mb0 {\n  margin-bottom: 0rem !important;\n}\n.ml0 {\n  margin-left: 0rem !important;\n}\n.mr0 {\n  margin-right: 0rem !important;\n}\n.m0 {\n  margin: 0rem 0rem 0rem 0rem !important;\n}\n.mx0 {\n  margin-left: 0rem !important;\n  margin-right: 0rem !important;\n}\n.my0 {\n  margin-top: 0rem !important;\n  margin-bottom: 0rem !important;\n}\n.mt1 {\n  margin-top: 1rem !important;\n}\n.mb1 {\n  margin-bottom: 1rem !important;\n}\n.ml1 {\n  margin-left: 1rem !important;\n}\n.mr1 {\n  margin-right: 1rem !important;\n}\n.m1 {\n  margin: 1rem 1rem 1rem 1rem !important;\n}\n.mx1 {\n  margin-left: 1rem !important;\n  margin-right: 1rem !important;\n}\n.my1 {\n  margin-top: 1rem !important;\n  margin-bottom: 1rem !important;\n}\n.mt2 {\n  margin-top: 2rem !important;\n}\n.mb2 {\n  margin-bottom: 2rem !important;\n}\n.ml2 {\n  margin-left: 2rem !important;\n}\n.mr2 {\n  margin-right: 2rem !important;\n}\n.m2 {\n  margin: 2rem 2rem 2rem 2rem !important;\n}\n.mx2 {\n  margin-left: 2rem !important;\n  margin-right: 2rem !important;\n}\n.my2 {\n  margin-top: 2rem !important;\n  margin-bottom: 2rem !important;\n}\n.mt05 {\n  margin-top: 0.5rem !important;\n}\n.mb05 {\n  margin-bottom: 0.5rem !important;\n}\n.ml05 {\n  margin-left: 0.5rem !important;\n}\n.mr05 {\n  margin-right: 0.5rem !important;\n}\n.m05 {\n  margin: 0.5rem 0.5rem 0.5rem 0.5rem !important;\n}\n.mx05 {\n  margin-left: 0.5rem !important;\n  margin-right: 0.5rem !important;\n}\n.my05 {\n  margin-top: 0.5rem !important;\n  margin-bottom: 0.5rem !important;\n}\n.mt025 {\n  margin-top: 0.25rem !important;\n}\n.mb025 {\n  margin-bottom: 0.25rem !important;\n}\n.ml025 {\n  margin-left: 0.25rem !important;\n}\n.mr025 {\n  margin-right: 0.25rem !important;\n}\n.m025 {\n  margin: 0.25rem 0.25rem 0.25rem 0.25rem !important;\n}\n.mx025 {\n  margin-left: 0.25rem !important;\n  margin-right: 0.25rem !important;\n}\n.my025 {\n  margin-top: 0.25rem !important;\n  margin-bottom: 0.25rem !important;\n}\n.pt0 {\n  padding-top: 0rem !important;\n}\n.pb0 {\n  padding-bottom: 0rem !important;\n}\n.pl0 {\n  padding-left: 0rem !important;\n}\n.pr0 {\n  padding-right: 0rem !important;\n}\n.p0 {\n  padding: 0rem 0rem 0rem 0rem !important;\n}\n.px0 {\n  padding-left: 0rem !important;\n  padding-right: 0rem !important;\n}\n.py0 {\n  padding-top: 0rem !important;\n  padding-bottom: 0rem !important;\n}\n.pt1 {\n  padding-top: 1rem !important;\n}\n.pb1 {\n  padding-bottom: 1rem !important;\n}\n.pl1 {\n  padding-left: 1rem !important;\n}\n.pr1 {\n  padding-right: 1rem !important;\n}\n.p1 {\n  padding: 1rem 1rem 1rem 1rem !important;\n}\n.px1 {\n  padding-left: 1rem !important;\n  padding-right: 1rem !important;\n}\n.py1 {\n  padding-top: 1rem !important;\n  padding-bottom: 1rem !important;\n}\n.pt2 {\n  padding-top: 2rem !important;\n}\n.pb2 {\n  padding-bottom: 2rem !important;\n}\n.pl2 {\n  padding-left: 2rem !important;\n}\n.pr2 {\n  padding-right: 2rem !important;\n}\n.p2 {\n  padding: 2rem 2rem 2rem 2rem !important;\n}\n.px2 {\n  padding-left: 2rem !important;\n  padding-right: 2rem !important;\n}\n.py2 {\n  padding-top: 2rem !important;\n  padding-bottom: 2rem !important;\n}\n.pt05 {\n  padding-top: 0.5rem !important;\n}\n.pb05 {\n  padding-bottom: 0.5rem !important;\n}\n.pl05 {\n  padding-left: 0.5rem !important;\n}\n.pr05 {\n  padding-right: 0.5rem !important;\n}\n.p05 {\n  padding: 0.5rem 0.5rem 0.5rem 0.5rem !important;\n}\n.px05 {\n  padding-left: 0.5rem !important;\n  padding-right: 0.5rem !important;\n}\n.py05 {\n  padding-top: 0.5rem !important;\n  padding-bottom: 0.5rem !important;\n}\n.pt025 {\n  padding-top: 0.25rem !important;\n}\n.pb025 {\n  padding-bottom: 0.25rem !important;\n}\n.pl025 {\n  padding-left: 0.25rem !important;\n}\n.pr025 {\n  padding-right: 0.25rem !important;\n}\n.p025 {\n  padding: 0.25rem 0.25rem 0.25rem 0.25rem !important;\n}\n.px025 {\n  padding-left: 0.25rem !important;\n  padding-right: 0.25rem !important;\n}\n.py025 {\n  padding-top: 0.25rem !important;\n  padding-bottom: 0.25rem !important;\n}\n.font-small-1 {\n  font-size: 0.857142857142857rem;\n}\n.font-large-1 {\n  font-size: 1.142857142857143rem;\n}\n.font-small-2 {\n  font-size: 0.714285714285714rem;\n}\n.font-large-2 {\n  font-size: 1.285714285714286rem;\n}\n.font-small-3 {\n  font-size: 0.571428571428571rem;\n}\n.font-large-3 {\n  font-size: 1.428571428571429rem;\n}\n.font-small-4 {\n  font-size: 0.428571428571429rem;\n}\n.font-large-4 {\n  font-size: 1.571428571428571rem;\n}\n.List-Sortable--Pa46P .List-Item--12uYs {\n  border: 1px solid #dce0e0;\n  border-radius: 2px;\n  margin-bottom: 0.5rem;\n  background: #f5f8fa;\n}\n.bp3-dark .List-Sortable--Pa46P .List-Item--12uYs {\n  border-color: #394b59;\n  background: #394b59;\n}\n.List-Item--12uYs {\n  display: flex;\n  align-items: center;\n  background: #fff;\n}\n.List-Item--12uYs > * + * {\n  margin-left: 0.5rem;\n}\n.List-RemoveButton--2yC3c {\n  margin-right: 0.25rem;\n}\n", '' ]]);
const { RSL_COMPONENT } = require("!../../node_modules/@pinto/react-style-loader/react-style-loader.runtime.compiled.js");
const __Sortable = RSL_COMPONENT('Sortable', 'ListSortable', 'List-Sortable--Pa46P');
 const Sortable = __Sortable
const __Item = RSL_COMPONENT('Item', 'ListItem', 'List-Item--12uYs');
 const Item = __Item
const __RemoveButton = RSL_COMPONENT('RemoveButton', 'ListRemoveButton', 'List-RemoveButton--2yC3c');
 const RemoveButton = __RemoveButton

